import { getSwiperInstance } from '@/helpers/swiperInstance';
import { checkVolumeButtonState } from '@/helpers/volumeControl';
import { animateCurrentProgressBarInElement, prepareCurrentProgressBarInElement, resetInactiveSlidesProgressBars } from '@/modules/actions/headerLineDuration';
import { showMediaLoadingShimmer, hideMediaLoadingShimmer } from '@/helpers/mediaLoadingShimmer';
import { navigateMediaItem } from '@/helpers/navigateMediaItem';
import { mobileAutoPlayNextAction } from '@/modules/actions/mobile/mobileAutoPlayNextAction';
import { trackStoreeelEvent } from "@/helpers/trackStoreeelEvent";
import { buildMediaAssetViewParams } from "@/helpers/buildMediaAssetViewParams";
import { buildMediaAssetIdParam } from "@/helpers/buildMediaAssetIdParam";
import { isTrackableMediaId } from "@/helpers/isTrackableMediaId";
import localStorageRepositories from "@/plugins/localStorageRepositories";
import pauseImg from '@/assets/images/pause-fill.svg';
import { updateGroupSeenBorder } from "@/helpers/updateGroupSeenBorder";
import { queryHeaderElementsInElement } from "@/helpers/headerShadow";
import { updateCtaButtonAction } from '@/modules/actions/mobile/updateCtaButtonAction';
import { ensureReelSlideMediaLoaded, loadReelMediaWindow } from '@/helpers/reelMediaLoadWindow';
import { cancelPendingImageSwap, clearImageTimer, isMediaImageReady, preloadNextStoryVideo, resetStorySlideMedia, setMediaImageContent, swapStoryVideo } from '@/helpers/mediaUtils';
import { setAdBadge } from '@/helpers/adBadge';
import { applyMediaLongPressHandlers } from '@/modules/actions/mobile/applyMediaLongPressHandlers';
import { maybeShowTargetingQuestionnaireForCurrentContent } from '@/helpers/maybeShowTargetingQuestionnaire';
import { isReelLike } from '@/helpers/isReelLike';
import { injectFreqInsertIntoGroup, unmarkUnseenFreqInserts, recordDisplayedContent } from '@/helpers/streamsTargeting';
import { headerLine } from '@/modules/parts/headerLine';
import { syncFeedFolderAspectFrame } from '@/helpers/feedFolderAspectFrame';

const ensureActiveMediaHandlers = () => {
  window.requestAnimationFrame(() => {
    const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
    if (!activeSlide) return;
    activeSlide.querySelectorAll('video, img').forEach((media) => {
      applyMediaLongPressHandlers(media);
    });
  });
};

export const groupSwipeAction = () => {
  const swiper = getSwiperInstance();

  // モーダル初期化ごとに先頭強制フラグをリセットする。
  // （applyCircleActiveSlideState が早期 return した場合などにフラグが残り、
  //  モーダル再オープン後の最初のグループ移動へ持ち越すのを防ぐ。circle 専用フラグ）
  window._storeeelCircleForceFirstContent = false;

  if (window.storeeelItems.frame === "circle") {
    // groupSwipeAction 呼び出し時点で window.iconId には最初のグループIDが入っている。
    // _storeeelLastRealGroupId は slideChangeTransitionEnd（=スワイプ後）でしかセットされないため、
    // 初回スワイプ先が freq insert スライドだと未定義のまま CTA クリックされて group_id が null になる。
    // ここで初期値として保持しておくことで、最初のスワイプが freq insert であっても正しく参照できる。
    if (window.iconId) {
      window._storeeelLastRealGroupId = String(window.iconId);
    }

    const CIRCLE_SWIPING_CLASS = 'storeeel--swiping';
    const getCircleRoot = () => document.querySelector('.storeeel-modal > .storeeel-swiper-group-wrapper');

    let circleIsSwiping = false;
    let circleSwipingReleaseTimerId = null;

    const getGroupIndex = () => {
      const s = getSwiperInstance();
      return Number.isFinite(s?.realIndex) ? s.realIndex : (s?.activeIndex ?? 0);
    };

    const getGroupsCount = () => (window.storeeelItems?.contentGroups || []).length;

    const getGroupIndexFromActiveSlide = (activeSlide) => {
      // loop 有効時、slideChange のタイミングによって swiper.realIndex と DOM のアクティブスライドがズレることがあるため、
      // data-swiper-slide-index（元スライドのindex）を優先してグループindexを確定する。
      const idxAttr = activeSlide?.getAttribute?.('data-swiper-slide-index');
      const idx = idxAttr != null ? Number(idxAttr) : NaN;
      if (Number.isFinite(idx)) return idx;
      return getGroupIndex();
    };

    const getGroupIdFromSlide = (slide) => {
      const wrapper = slide?.querySelector?.('.slide-media-wrapper');
      const groupId = wrapper?.getAttribute?.('group-id');
      if (groupId != null && String(groupId).length > 0) return String(groupId);
      const groups = window.storeeelItems?.contentGroups || [];
      const groupIndex = getGroupIndexFromActiveSlide(slide);
      const group = groups[groupIndex];
      return group?.id != null ? String(group.id) : null;
    };

    // SPはスライド毎に固定ヘッダーが生成されるため、loop（複製スライド）時に非アクティブのヘッダーが前面に残って
    // 「区切り数が違う」ように見えることがある。基本はアクティブのみ表示し、スワイプ中だけ前後も表示する。
    const updateActiveHeaderVisibility = (options = {}) => {
      if (!window.storeeelItems?.isMobile) return;

      const showNeighbors = typeof options.showNeighbors === 'boolean'
        ? options.showNeighbors
        : circleIsSwiping;

      const slidesToShow = showNeighbors
        ? Array.from(document.querySelectorAll(
          '.storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-active,' +
          '.storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-next,' +
          '.storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-prev'
        ))
        : [document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active')].filter(Boolean);

      // ここで「全ヘッダー非表示」にしてしまうと、activeが一瞬外れたタイミングでヘッダーが消えっぱなしになるため、
      // 表示対象が確定できない場合は何もしない。
      if (slidesToShow.length === 0) return;

      const allWrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .storeeel-header-wrapper');
      allWrappers.forEach((w) => { w.style.display = 'none'; });
      slidesToShow.forEach((slide) => {
        slide.querySelectorAll('.storeeel-header-wrapper').forEach((w) => { w.style.display = ''; });
      });
    };

    // cube用: アクティブ以外のスライド内メディアを非表示にする
    const hideNonActiveSlideMedia = () => {
      const slides = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide');
      const count = getGroupsCount();
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      const active = getGroupIndexFromActiveSlide(activeSlide);
      // loop 有効時は端↔先頭が隣接するため wrap する。loop 無効時に wrap すると、
      // 5グループ時などに「同一キューブ面（index 差4）の端スライド」を隣接として表示対象に残してしまい、
      // 下記の cube 面の回り込み対策が効かなくなるため、loop 無効時は wrap しない。
      const isLoop = Boolean(getSwiperInstance()?.params?.loop);
      const prev = count > 1
        ? (isLoop ? (active - 1 + count) % count : Math.max(0, active - 1))
        : active;
      const next = count > 1
        ? (isLoop ? (active + 1) % count : Math.min(count - 1, active + 1))
        : active;
      slides.forEach((slide, domIndex) => {
        // loop 有効時は複製スライドが混ざるため data-swiper-slide-index（元スライドのindex）で判定する。
        // loop 無効時は data-swiper-slide-index が付与されず、以前は idx=null → 常に keepVisible となって
        // 非アクティブのメディアが display 表示のまま残っていた。iPhone は cube の preserve-3d を無効化して
        // いるため、同一キューブ面（index 差が4）のスライドがアクティブ面へ回り込み、子要素の
        // visibility:visible が wrapper の visibility:hidden を打ち消して「7個目で3個目のコンテンツが見える」
        // 不具合になっていた。data-swiper-slide-index が無い場合は DOM 順（loop 無効なので = スライド/
        // グループ index）で判定し、アクティブ・前後以外のメディアを確実に隠す。
        const idxAttr = slide.getAttribute('data-swiper-slide-index');
        let isKeepVisible;
        if (idxAttr != null) {
          // loop 有効時の通常経路。従来と同じく元スライドの index で判定する。
          const idx = Number(idxAttr);
          isKeepVisible = idx === active || idx === prev || idx === next;
        } else if (isLoop) {
          // loop 有効なのに index 属性が無い異例ケースは、従来どおり表示側へ倒す（誤って隠さない）。
          isKeepVisible = true;
        } else {
          // loop 無効: DOM 順 = スライド/グループ index として判定し、非アクティブのメディアを確実に隠す。
          isKeepVisible = domIndex === active || domIndex === prev || domIndex === next;
        }
        slide.querySelectorAll('.slide-media-wrapper video, .slide-media-wrapper img').forEach((el) => {
          if (isKeepVisible) {
            // 近傍（前後）とアクティブは表示
            // ※ グループ内のメディア切替で display:none を使うため、ここでは「この関数が隠した要素」だけ戻す
            if (el.dataset.storeeelHiddenByCube === '1') {
              el.style.removeProperty('display');
              delete el.dataset.storeeelHiddenByCube;
            }
          } else {
            if (el.style.display !== 'none') {
              el.dataset.storeeelHiddenByCube = '1';
            }
            el.style.display = 'none';
          }
        });
      });
    };

    const beginCircleSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      if (circleSwipingReleaseTimerId != null) {
        clearTimeout(circleSwipingReleaseTimerId);
        circleSwipingReleaseTimerId = null;
      }
      circleIsSwiping = true;
      getCircleRoot()?.classList?.add?.(CIRCLE_SWIPING_CLASS);
      updateActiveHeaderVisibility({ showNeighbors: true });
    };

    const endCircleSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      // まだタッチ中（押下中）に解除してしまうと、ゆっくりスワイプ中に表示が戻ってしまうためガードする
      const swiperInstance = getSwiperInstance();
      if (swiperInstance?.touchEventsData?.isTouched) return;
      if (circleSwipingReleaseTimerId != null) {
        clearTimeout(circleSwipingReleaseTimerId);
        circleSwipingReleaseTimerId = null;
      }
      circleIsSwiping = false;
      getCircleRoot()?.classList?.remove?.(CIRCLE_SWIPING_CLASS);
      updateActiveHeaderVisibility({ showNeighbors: false });
    };

    const scheduleEndCircleSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      if (circleSwipingReleaseTimerId != null) clearTimeout(circleSwipingReleaseTimerId);
      // touchEnd 後も snap の transition が残るため、少し遅らせて解除する
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      circleSwipingReleaseTimerId = window.setTimeout(() => {
        circleSwipingReleaseTimerId = null;
        endCircleSwiping();
      }, speed + 50);
    };

    // グループIDに対応するスライドのヘッダーを再構築する（circle 内 freq insert 注入後に呼ぶ）
    const rebuildSlideHeaderForGroup = (groupId, group) => {
      const allSlides = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide');
      allSlides.forEach((slide) => {
        const mediaWrapper = slide.querySelector('.slide-media-wrapper');
        if (!mediaWrapper) return;
        if (String(mediaWrapper.getAttribute('group-id')) !== String(groupId)) return;
        const oldHeader = slide.querySelector('[data-storeeel-header-host]');
        if (oldHeader) oldHeader.remove();
        slide.prepend(headerLine(group.contents.length, group.name || ''));
      });
    };

    let lastActiveGroupId = getGroupIdFromSlide(
      document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active')
    );
    // 同一遷移内での重複計測を避けるためのガード
    let lastTrackedViewKey = null;
    let lastTrackedViewSeq = null;
    // 直近のグループ移動が前進方向だったか。ループの「最後↔先頭」は index 比較では判別できない
    // （特に2グループでは前進も後退も同じ index に着地する）ため、実際のスワイプ/操作方向を保持する。
    // slideChangeTransitionStart で activeIndex/previousIndex（スワイプ・タップ両対応）から、
    // 端ドラッグ離し経路では touchEnd の swipeDirection から更新する。
    let lastGroupSwipeIsForward = null;

    // グループ遷移時の freq 注入処理。
    // スワイプの仕方（フリック / 端までドラッグして離す）によって発火するイベントが変わるため、
    // slideChangeTransitionStart / slideChangeTransitionEnd / touchEnd後の finalizeAfterRelease の
    // すべての経路から本関数を呼び、どの経路でも確実に注入されるようにする。
    // 注入は group._freqInsertInjected フラグで冪等化しているため、複数経路から呼ばれても二重注入しない。
    // カウント（contentsSeen）と閲覧済み判定は表示時（recordDisplayedContent）に積むため、ここでは行わない。
    const runFreqGroupTransition = (prevGroupId, landedGroupId) => {
      if (!prevGroupId || !landedGroupId || String(prevGroupId) === String(landedGroupId)) return;
      // 先頭強制フラグは全経路共通で立てる（途中位置から再生されるのを防ぐ）
      window._storeeelCircleForceFirstContent = true;

      const freqState = window._storeeelFreqInsertCircleState;
      if (!freqState) return;
      const groups = window.storeeelItems?.contentGroups || [];
      const prevGroupIdx = groups.findIndex((g) => String(g.id) === String(prevGroupId));
      const nextGroupIdx = groups.findIndex((g) => String(g.id) === String(landedGroupId));
      // 実際の移動方向を最優先。取得できない場合のみ index 比較にフォールバックする。
      const indexForward = nextGroupIdx > prevGroupIdx
        || (nextGroupIdx === 0 && prevGroupIdx === groups.length - 1);
      const isForward = typeof lastGroupSwipeIsForward === 'boolean' ? lastGroupSwipeIsForward : indexForward;

      // 後退（戻る）方向ではグループに freq を注入しない（進行方向に動いた時のみ表示する）
      if (!isForward) return;

      // 前グループの未到達位置に残っている freq を物理除去（戻った時に表示されないように）
      if (prevGroupIdx >= 0) {
        const prevMoviePosition = Number(window.moviePosition || 0);
        const beforeLen = groups[prevGroupIdx]?.contents?.length;
        unmarkUnseenFreqInserts(groups[prevGroupIdx], prevMoviePosition, freqState);
        // freq 除去でコンテンツ数が減った場合、前グループのヘッダー（進捗バー）は
        // 開いた時点の freq込み本数のまま残るため、戻った時にバー数とコンテンツ数が
        // ズレて「c7,c8 がスキップされ次グループへ飛ぶ」ように見える。データに合わせて再構築する。
        if (groups[prevGroupIdx]?.contents?.length !== beforeLen) {
          window.storeeelItems = { ...window.storeeelItems, contentGroups: groups };
          rebuildSlideHeaderForGroup(String(prevGroupId), groups[prevGroupIdx]);
        }
      }
      // 直近表示が freq だったら次グループ先頭への pre-insert をスキップ（freqの連続表示を防止）
      freqState.skipNextPreInsert = freqState.lastDisplayedWasFreq === true;

      if (nextGroupIdx >= 0 && !groups[nextGroupIdx]._freqInsertInjected) {
        const injectedGroup = injectFreqInsertIntoGroup(groups[nextGroupIdx], freqState);
        injectedGroup._freqInsertInjected = true;
        groups[nextGroupIdx] = injectedGroup;
        window.storeeelItems = { ...window.storeeelItems, contentGroups: groups };
        rebuildSlideHeaderForGroup(String(landedGroupId), injectedGroup);
        // c1フラッシュ防止: 注入後の先頭が freq ならアクティブスライドDOMを即反映する
        const firstContent = injectedGroup.contents[0];
        if (firstContent?.isFrequencyInsertContent) {
          const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          const activeWrapper = activeSlide?.querySelector('.slide-media-wrapper');
          if (activeWrapper) resetStorySlideMedia(activeWrapper, firstContent);
        }
      }
    };

    const resetInactiveGroupToFirstContent = (groupId) => {
      if (!groupId) return;
      const groups = window.storeeelItems?.contentGroups || [];
      const group = groups.find(g => String(g?.id) === String(groupId));
      const firstContent = group?.contents?.[0];
      if (!firstContent) return;

      const wrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .slide-media-wrapper');
      wrappers.forEach((wrapperEl) => {
        const wrapperGroupId = wrapperEl.getAttribute('group-id');
        if (String(wrapperGroupId) !== String(groupId)) return;
        const parentSlide = wrapperEl.closest('.swiper-slide');
        if (parentSlide?.classList?.contains('swiper-slide-active')) return;
        resetStorySlideMedia(wrapperEl, firstContent);
      });
    };

    // 初期適用
    hideNonActiveSlideMedia();
    updateActiveHeaderVisibility({ showNeighbors: false });
    ensureActiveMediaHandlers();
    // スライド毎の進捗開始の多重起動防止用シーケンス
    window._storeeelProgressSeq = (window._storeeelProgressSeq || 0);
    const startProgressForActive = (seq) => {
      const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!currentSlide) return;
      const wrapper = currentSlide.querySelector('.slide-media-wrapper');
      showMediaLoadingShimmer(wrapper);
      const video = currentSlide.querySelector('.slide-media-wrapper video.storeeel-movie');
      const image = currentSlide.querySelector('.slide-media-wrapper img.storeeel-media-image');

      const mediaIndex = window.moviePosition || 0;

      if (video && video.style.display !== 'none') {
        if (video._onLoadedMetadataForProgress) {
          video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
          video._onLoadedMetadataForProgress = null;
        }
        if (video._onCanPlayForProgress) {
          video.removeEventListener('canplay', video._onCanPlayForProgress);
          video._onCanPlayForProgress = null;
        }
        if (video._onPlayingForProgress) {
          video.removeEventListener('playing', video._onPlayingForProgress);
          video._onPlayingForProgress = null;
        }
        if (video._storeeelProgressFallbackTimerId != null) {
          clearTimeout(video._storeeelProgressFallbackTimerId);
          video._storeeelProgressFallbackTimerId = null;
        }

        const progressSeq = Number.isFinite(seq) ? seq : window._storeeelProgressSeq;
        let progressStarted = false;
        const isStillActive = () => {
          if (progressSeq !== window._storeeelProgressSeq) return false;
          const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          return !!activeSlide && activeSlide === currentSlide && activeSlide.contains(video);
        };

        const cleanup = () => {
          if (video._onLoadedMetadataForProgress) {
            video.removeEventListener('loadedmetadata', video._onLoadedMetadataForProgress);
            video._onLoadedMetadataForProgress = null;
          }
          if (video._onCanPlayForProgress) {
            video.removeEventListener('canplay', video._onCanPlayForProgress);
            video._onCanPlayForProgress = null;
          }
          if (video._onPlayingForProgress) {
            video.removeEventListener('playing', video._onPlayingForProgress);
            video._onPlayingForProgress = null;
          }
          // ポーリングのフォールバックも止める
          if (video._storeeelProgressFallbackTimerId != null) {
            clearTimeout(video._storeeelProgressFallbackTimerId);
            video._storeeelProgressFallbackTimerId = null;
          }
        };

        const startProgress = (duration) => {
          if (!isStillActive()) return;
          animateCurrentProgressBarInElement(duration, mediaIndex, currentSlide);
          hideMediaLoadingShimmer(wrapper);
          video.classList.add('active');
          progressStarted = true;
        };

        const tryStartProgress = () => {
          if (progressStarted) return true;
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(wrapper);
            return false;
          }
          if (video.paused || video.ended) return false;
          if (video.readyState < 2) return false;
          const duration = Number(video.duration);
          if (!Number.isFinite(duration) || duration <= 0) return false;
          startProgress(duration);
          return true;
        };

        const onReady = () => {
          if (tryStartProgress()) {
            cleanup();
          }
        };

        // イベント（loadedmetadata/canplay/playing）が取りこぼされても確実に開始できるよう、
        // 再生が始まっていなければポーリングでも監視し、止まっていれば再生を促す。
        const pollProgress = () => {
          video._storeeelProgressFallbackTimerId = null;
          if (progressStarted) return;
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(wrapper);
            return;
          }
          // 自動再生がブロックされて止まっている場合は再生を促す（ミュート動画なので再生可）。
          // ただしアンケート表示中は再生をロックしているため、その間は再生しない。
          const questionnaireLocked = window.__storeeelQuestionnairePlaybackLocked === true
            || Boolean(document.querySelector('[data-storeeel-questionnaire-modal="true"]'));
          if (video.paused && !video.ended && !questionnaireLocked) {
            try {
              const p = video.play?.();
              if (p?.catch) p.catch(() => { void 0; });
            } catch { void 0; }
          }
          if (tryStartProgress()) {
            cleanup();
            return;
          }
          video._storeeelProgressFallbackTimerId = window.setTimeout(pollProgress, 200);
        };

        // 再生が始まるまで進捗を開始しない
        prepareCurrentProgressBarInElement(mediaIndex, currentSlide);
        video._onLoadedMetadataForProgress = onReady;
        video._onCanPlayForProgress = onReady;
        video._onPlayingForProgress = onReady;
        video.addEventListener('loadedmetadata', onReady);
        video.addEventListener('canplay', onReady);
        video.addEventListener('playing', onReady);
        onReady();
        if (!progressStarted) {
          video._storeeelProgressFallbackTimerId = window.setTimeout(pollProgress, 200);
        }
      } else if (image && image.style.display !== 'none') {
        if (image._onLoadForProgress) {
          image.removeEventListener('load', image._onLoadForProgress);
          image._onLoadForProgress = null;
        }
        if (image._onErrorForProgress) {
          image.removeEventListener('error', image._onErrorForProgress);
          image._onErrorForProgress = null;
        }

        const isStillActive = () => {
          const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          return !!activeSlide && activeSlide === currentSlide && activeSlide.contains(image);
        };

        const cleanup = () => {
          if (image._onLoadForProgress) {
            image.removeEventListener('load', image._onLoadForProgress);
            image._onLoadForProgress = null;
          }
          if (image._onErrorForProgress) {
            image.removeEventListener('error', image._onErrorForProgress);
            image._onErrorForProgress = null;
          }
        };

        const startProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(wrapper);
            return;
          }
          animateCurrentProgressBarInElement(5, mediaIndex, currentSlide);
          hideMediaLoadingShimmer(wrapper);
          image.classList.add('active');
          cleanup();
        };

        const tryStartProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(wrapper);
            return false;
          }
          if (!isMediaImageReady(image)) return false;
          startProgress();
          return true;
        };

        // 読み込み完了後に進捗を開始する
        prepareCurrentProgressBarInElement(mediaIndex, currentSlide);
        image._onLoadForProgress = () => {
          image.dataset.storeeelPendingLoad = '0';
          tryStartProgress();
        };
        image._onErrorForProgress = () => {
          image.dataset.storeeelPendingLoad = '0';
          cleanup();
        };
        image.addEventListener('load', image._onLoadForProgress);
        image.addEventListener('error', image._onErrorForProgress);
        tryStartProgress();
      }
    };

    const applyCircleActiveSlideState = (options = {}) => {
      // skipIfReady: ensure（復旧目的）からの冗長な再呼び出し時のみ true。
      // 既に正しく初期化済みなら何もしない＝進捗開始を中断しない。transitionEnd の直呼びは常にフル初期化する。
      const { skipIfReady = false } = options;
      const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!currentSlide) return;

      // 離脱したスライドの進捗バーを止める（裏で進み続けたバーが戻った時に一瞬見えるのを防ぐ）
      resetInactiveSlidesProgressBars(currentSlide);

      // 遷移後は常にアクティブだけ表示（固定ヘッダーの重なりを避ける）
      updateActiveHeaderVisibility({ showNeighbors: false });

      const currentWrapper = currentSlide.querySelector('.slide-media-wrapper');
      if (!currentWrapper) return;

      const groups = window.storeeelItems?.contentGroups || [];
      const pendingIndex = Number.isFinite(window._storeeelPendingGroupSlideIndex)
        ? window._storeeelPendingGroupSlideIndex
        : null;
      if (pendingIndex != null) {
        const pendingGroupId = groups[pendingIndex]?.id != null ? String(groups[pendingIndex].id) : null;
        const currentGroupId = String(currentWrapper.getAttribute('group-id') || '').split('-').pop();
        if (pendingGroupId && currentGroupId !== pendingGroupId) {
          const s = getSwiperInstance();
          if (!s?.animating) {
            if (typeof s?.slideToLoop === 'function') {
              s.slideToLoop(pendingIndex, 0);
            } else if (typeof s?.slideTo === 'function') {
              s.slideTo(pendingIndex, 0);
            }
          }
          return;
        }
      }
      const groupIdFromDom = currentWrapper.getAttribute('group-id');
      const groupIndexFromDom = groupIdFromDom == null
        ? -1
        : groups.findIndex(g => String(g?.id) === String(groupIdFromDom));
      const groupIndex = groupIndexFromDom !== -1 ? groupIndexFromDom : getGroupIndexFromActiveSlide(currentSlide);
      const group = groups[groupIndex];
      if (!group) return;

      const normalizedGroupId = String(group.id);
      const requestedMoviePosition = Number(window.moviePosition);
      // グループ遷移直後は先頭コンテンツから再生する。ensure の再実行で iconId==group となっても
      // 書き換わった位置を「維持」しないよう、強制フラグ中は維持判定を無効化する。
      const forceFirstContent = window._storeeelCircleForceFirstContent === true;
      const shouldKeepCurrentPosition =
        !forceFirstContent
        && String(window.iconId || '').split('-').pop() === normalizedGroupId
        && Number.isInteger(requestedMoviePosition)
        && requestedMoviePosition >= 0
        && requestedMoviePosition < group.contents.length;
      const desiredPosition = shouldKeepCurrentPosition ? requestedMoviePosition : 0;

      // 冪等ガード: アクティブスライドが既に「正しいグループ・正しいコンテンツで再生中 or 進捗開始待ち」なら、
      // 動画の再 swap / window._storeeelProgressSeq のインクリメント / 進捗監視のやり直しを行わずに早期 return する。
      // これがないと transitionEnd 直呼び + ensure の多重再呼びの度に progressSeq が進み、
      // 先行の startProgressForActive が中断され続けて「再生されない/進捗バーが始まらない」状態になる（特にゆっくりスワイプ）。
      const expectedContentId = String(group.contents[desiredPosition]?.id ?? '');
      const wrapperGroupId = String(currentWrapper.getAttribute('group-id') || '').split('-').pop();
      const barsExist = queryHeaderElementsInElement('.storeeel-movie-select_banner', currentSlide).length > 0;
      const guardVideo = currentWrapper.querySelector('video.storeeel-movie');
      const guardImage = currentWrapper.querySelector('img.storeeel-media-image');
      // 「準備済み」は進捗が実際に開始(active bar) or 開始待ち(リスナー/タイマー)であること。
      // メディア要素自体の 'active' クラスは resetInactiveGroupToFirstContent の事前ロードでも付与されるため信頼しない。
      const activeBarExists = queryHeaderElementsInElement('.storeeel-movie-select_banner.active', currentSlide).length > 0;
      const isVideoAlreadyReady = !!guardVideo
        && guardVideo.style.display !== 'none'
        && String(guardVideo.dataset.storeeelContentId || '') === expectedContentId
        && (
          activeBarExists
          || guardVideo._storeeelProgressFallbackTimerId != null
          || !!guardVideo._onPlayingForProgress
          || !!guardVideo._onLoadedMetadataForProgress
        );
      const isImageAlreadyReady = !!guardImage
        && guardImage.style.display !== 'none'
        && String(guardImage.getAttribute('content-id') || '') === expectedContentId
        && (activeBarExists || guardImage._onLoadForProgress != null);
      if (
        skipIfReady
        && expectedContentId
        && barsExist
        && wrapperGroupId === normalizedGroupId
        && (isVideoAlreadyReady || isImageAlreadyReady)
      ) {
        // 既に正しく初期化済み。意図（先頭から再生）を達成済みなのでフラグをクリアして no-op で返す。
        window._storeeelCircleForceFirstContent = false;
        return;
      }

      // 同じグループの再初期化では現在位置を維持し、実際のグループ切替時だけ先頭へ戻す
      clearImageTimer();
      window.moviePosition = desiredPosition;
      window.iconId = normalizedGroupId;
      // プロモーションコンテンツのCTA計測で前後のグループIDを参照するために保持する
      window._storeeelLastRealGroupId = normalizedGroupId;
      // 先頭強制フラグは位置を確定したら消費済みとしてクリアする
      window._storeeelCircleForceFirstContent = false;

      const contents = navigateMediaItem();
      const targetMedia = Array.isArray(contents) ? contents[window.moviePosition] : null;
      if (!targetMedia?.mediaAsset?.attachmentData?.metadata?.mime_type) return;

      // グループ入場/遷移でアクティブになったコンテンツを表示時点で記録する（通常=カウント / freq=閲覧済み）
      recordDisplayedContent(targetMedia, window._storeeelFreqInsertCircleState);

      const mimeType = targetMedia.mediaAsset.attachmentData.metadata.mime_type;
      const currentVideo = currentSlide.querySelector('.storeeel-movie');
      const currentImage = currentSlide.querySelector('.storeeel-media-image');

      // 動画と画像からactiveクラスを外す（後続の自動送り判定に影響するため）
      if (currentVideo) currentVideo.classList.remove('active');
      if (currentImage) currentImage.classList.remove('active');

      currentWrapper.dataset.assetType = targetMedia?.mediaAsset?.type || '';

      if (window.storeeelItems?.isMobile) {
        document.querySelectorAll('.storeeel-swiper-group-wrapper .slide-media-wrapper').forEach((wrapperEl) => {
          if (wrapperEl !== currentWrapper && wrapperEl.dataset.assetType === 'adMediaAsset') {
            setAdBadge(wrapperEl, false);
          }
        });
      }
      setAdBadge(currentWrapper, targetMedia?.mediaAsset?.type === 'adMediaAsset');
      // グループ入場時に表示するコンテンツに合わせて、CTAの配置基準に使うフィードフォルダのマークを更新する
      // （同一グループ内に通常コンテンツと連携配信コンテンツが混在しうるため、入場のたびに同期する）
      syncFeedFolderAspectFrame(currentWrapper, targetMedia, currentImage || currentVideo);

      // このスライド用の進捗起動シーケンスを更新
      window._storeeelProgressSeq += 1;
      const progressSeq = window._storeeelProgressSeq;

      // すべての動画を停止・リセットし、スライドから active クラスを外す
      document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide').forEach((slide) => {
        slide.classList.remove('active');
        const video = slide.querySelector('video.storeeel-movie');
        if (video && video !== currentVideo) {
          video.pause();
          video.currentTime = 0;
        }
      });

      // 現在のスライドに active クラスを追加し、メディアを更新
      currentSlide.classList.add('active');
      if (mimeType.includes('video')) {
        if (currentImage) {
          cancelPendingImageSwap(currentImage);
          currentImage.style.display = 'none';
          currentImage.style.visibility = 'hidden';
          currentImage.style.opacity = '0';
        }

        if (window.storeeelItems?.isMobile && window.storeeelItems?.frame === 'circle') {
          const { activeVideo } = swapStoryVideo(currentWrapper, targetMedia, targetMedia.mediaAsset);
          if (activeVideo) {
            applyMediaLongPressHandlers(activeVideo);
          }
          checkVolumeButtonState();
          startProgressForActive(progressSeq);
        } else {
          if (!currentVideo) return;
          currentVideo.currentTime = 0;
          currentVideo.src = targetMedia.mediaAsset.mediaUrl;
          currentVideo.load(); // 明示的にロード
          currentVideo.style.display = 'block';
          currentVideo.style.visibility = 'visible';
          currentVideo.style.opacity = '1';
          currentVideo.classList.add('active');
          applyMediaLongPressHandlers(currentVideo);

          // 属性を最新化
          currentVideo.setAttribute('media-name', targetMedia.mediaAsset.name);
          currentVideo.setAttribute('media-id', targetMedia.mediaAsset.id);
          currentVideo.setAttribute('content-id', targetMedia.id);

          currentVideo.onloadeddata = () => {
            currentVideo.play().catch(() => { void 0; });
            checkVolumeButtonState();
          };
          // loadeddata が発火しない（キャッシュ/遅延/端末差）場合があるため、先に play を試みる
          try {
            const playPromise = currentVideo.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(() => { void 0; });
            }
          } catch { void 0; }
          checkVolumeButtonState();

          startProgressForActive(progressSeq);
        }
      } else if (mimeType.includes('image')) {
        if (!currentImage) return;
        currentImage.style.display = 'block';
        currentImage.style.visibility = 'visible';
        currentImage.classList.add('active');
        setMediaImageContent(currentImage, targetMedia, targetMedia.mediaAsset);

        if (currentVideo) {
          currentVideo.style.display = 'none';
          currentVideo.style.visibility = 'hidden';
          currentVideo.style.opacity = '0';
          try { currentVideo.pause(); } catch { void 0; }
        }

        startProgressForActive(progressSeq);
      }

      if (window.storeeelItems?.isMobile && window.storeeelItems?.frame === 'circle') {
        preloadNextStoryVideo(currentWrapper, contents);
      }

      if (pendingIndex != null) {
        // 期待したグループへ確定できたので、次の遷移判定に残らないようクリアする。
        window._storeeelPendingGroupSlideIndex = null;
      }

      updateCtaButtonAction(currentSlide);
      ensureActiveMediaHandlers();

      // 計測とローカルストレージ保存
      const firstContent = targetMedia;
      const firstAsset = firstContent?.mediaAsset;
      const contentId = firstContent?.id;
      // プロモーションは FrequencyInsertContent の ID を使い、通常は MediaAsset の ID を使う
      const mediaId = firstContent?.isFrequencyInsertContent
        ? (firstContent?.frequencyInsertContentId || firstAsset?.id)
        : firstAsset?.id;
      const trackKey = `${window.iconId || ''}:${mediaId ?? ''}:${contentId ?? ''}`;
      const shouldTrack = lastTrackedViewKey !== trackKey || lastTrackedViewSeq !== circleEnsureSeq;
      if (shouldTrack) {
        lastTrackedViewKey = trackKey;
        lastTrackedViewSeq = circleEnsureSeq;
        // グループ入場時の先頭が freq の場合のみ、通常視聴ではなく専用イベントで計測する。
        // commonChangeMediaProcess（グループ内送り、mediaUtils.js）と同じ振り分け。
        // フィードフォルダは mediaAsset.id が `{content hashid}:{商品ID}` の複合IDになっており、
        // 通常イベントのまま API 側で商品単位に集計されるため専用イベントは持たない。
        // media_id が空のイベントは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
        if (isTrackableMediaId(mediaId)) {
          if (firstContent?.isFrequencyInsertContent) {
            trackStoreeelEvent('storeeel_frequency_insert_media_view', {
              storeeel_name: firstAsset?.name,
              ...buildMediaAssetIdParam(mediaId, firstAsset?.type),
              campaign_id: window.storeeelItems.id,
              ...(window.iconId ? { group_id: window.iconId } : {}),
            });
          } else {
            trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
              storeeelName: firstAsset?.name,
              groupId: window.iconId,
              mediaId,
              campaignId: window.storeeelItems.id,
              mediaAssetType: firstAsset?.type,
            }));
          }
          localStorageRepositories.get('mediaView').set(
            mediaId,
            window.storeeelItems.id,
            window.iconId,
            firstAsset?.type ?? null
          );
        }
        // 無期限の既読フラグも保存（スライド型は保存しない）
        try {
          if (window.storeeelItems?.frame !== 'square') {
            if (contentId) {
              localStorageRepositories.get('contentSeen').set(contentId);
            }
            if (window.iconId) {
              updateGroupSeenBorder(window.iconId);
            }
          }
        } catch { void 0; }
      }
    };

    // Swiper(loop) では transitionEnd 直後に loopFix 等で active が差し替わり、
    // その場合に「再生/進捗/ヘッダー」が未初期化のまま残ることがあるため、
    // 状態が崩れている時だけ自動復旧する。
    const isElementVisible = (el) => {
      if (!el) return false;
      const style = window.getComputedStyle(el);
      if (style.display === 'none') return false;
      if (style.visibility === 'hidden') return false;
      const opacity = Number(style.opacity);
      if (Number.isFinite(opacity) && opacity <= 0) return false;
      return true;
    };

    const getCircleActiveSlideStatus = () => {
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!activeSlide) return null;
      const header = activeSlide.querySelector('.storeeel-header-wrapper');
      const headerVisible = !!header && window.getComputedStyle(header).display !== 'none';
      const bars = queryHeaderElementsInElement('.storeeel-movie-select_banner', activeSlide);
      const barsCount = bars.length;
      const hasActiveBar = queryHeaderElementsInElement('.storeeel-movie-select_banner.active', activeSlide).length > 0;
      const wrapper = activeSlide.querySelector('.slide-media-wrapper');
      const video = wrapper?.querySelector?.('video.storeeel-movie');
      const image = wrapper?.querySelector?.('img');
      const thumb = wrapper?.querySelector?.('.storeeel-video-thumbnail');
      const hasVisibleMedia = isElementVisible(video) || isElementVisible(image) || isElementVisible(thumb);
      // 進捗が「開始待ち」（再生開始イベント or ポーリング監視中）なら健全な状態。
      // ここで再初期化すると進捗開始をリセットしてしまい、かえって不安定になるため除外する。
      const progressPending = !!video && (
        video._storeeelProgressFallbackTimerId != null
        || !!video._onPlayingForProgress
        || !!video._onLoadedMetadataForProgress
      );

      return {
        headerVisible,
        barsCount,
        hasActiveBar,
        hasVisibleMedia,
        progressPending,
      };
    };

    const ensureCircleActiveSlideInitialized = (ensureSeq, remainingAttempts = 3) => {
      if (ensureSeq !== circleEnsureSeq) return;
      const swiperInstance = getSwiperInstance();
      if (swiperInstance?.animating) {
        if (remainingAttempts <= 0) return;
        window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, remainingAttempts), 50);
        return;
      }

      const status = getCircleActiveSlideStatus();
      if (!status) {
        if (remainingAttempts <= 0) return;
        window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, remainingAttempts - 1), 50);
        return;
      }

      const looksOk = status.headerVisible && status.barsCount > 0 && status.hasVisibleMedia
        && (status.hasActiveBar || status.progressPending);
      if (looksOk) return;
      if (remainingAttempts <= 0) return;

      // 復旧呼び出し。既に正しく初期化済みなら no-op（進捗開始を中断しない）。
      applyCircleActiveSlideState({ skipIfReady: true });
      hideNonActiveSlideMedia();
      updateActiveHeaderVisibility({ showNeighbors: false });
      ensureActiveMediaHandlers();
      mobileAutoPlayNextAction();

      window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, remainingAttempts - 1), 150);
    };

    let circleEnsureSeq = 0;
    let circleTransitionGuardTimerId = null;

    // ユーザーのゆっくりスワイプ中でも「隣面が真っ黒/ヘッダーが消える」見えを避ける
    swiper.on('touchStart', () => {
      beginCircleSwiping();
      hideNonActiveSlideMedia();
      ensureActiveMediaHandlers();
    });
    swiper.on('sliderMove', () => {
      if (!circleIsSwiping) beginCircleSwiping();
      hideNonActiveSlideMedia();
      ensureActiveMediaHandlers();
    });
    swiper.on('touchEnd', () => {
      scheduleEndCircleSwiping();
      // 端ドラッグ離し経路では slideChangeTransitionStart が発火しないことがあるため、
      // ここでスワイプ方向を捕捉して finalizeAfterRelease での方向判定に使う。
      if (swiper?.swipeDirection === 'next') lastGroupSwipeIsForward = true;
      else if (swiper?.swipeDirection === 'prev') lastGroupSwipeIsForward = false;
      // 指を離さず端までドラッグしてグループ移動した場合、snap アニメーションが発生せず
      // slideChangeTransitionEnd が発火しない → 移動先が初期化されず「再生/進捗が始まらない」ことがある。
      // 解放後に「アクティブグループと iconId の不一致（=未初期化）」を検出したら、transitionEnd 相当の初期化を行う。
      circleEnsureSeq += 1;
      const ensureSeq = circleEnsureSeq;
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      const finalizeAfterRelease = () => {
        if (ensureSeq !== circleEnsureSeq) return;
        const s = getSwiperInstance();
        if (s?.animating) {
          // snap アニメーション中ならその完了（transitionEnd）に委ねる
          return;
        }
        const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
        if (!activeSlide) return;
        const landedGroupIdFull = getGroupIdFromSlide(activeSlide);
        const activeGroupId = String(landedGroupIdFull || '').split('-').pop();
        const currentIconId = String(window.iconId || '').split('-').pop();
        if (activeGroupId && currentIconId !== activeGroupId) {
          // transitionEnd が発火せずに未初期化のまま留まっている。先頭から初期化する。
          const prevGroupId = lastActiveGroupId;
          window._storeeelCircleForceFirstContent = true;
          // 端までドラッグして離した場合は transitionStart/End が発火しないため、
          // この経路でも freq 注入を行う（applyCircleActiveSlideState より前に実行する）。
          runFreqGroupTransition(prevGroupId, landedGroupIdFull);
          applyCircleActiveSlideState();
          if (prevGroupId && String(prevGroupId).split('-').pop() !== activeGroupId) {
            resetInactiveGroupToFirstContent(prevGroupId);
          }
          lastActiveGroupId = getGroupIdFromSlide(
            document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active')
          );
          hideNonActiveSlideMedia();
          updateActiveHeaderVisibility({ showNeighbors: false });
          mobileAutoPlayNextAction();
        }
        ensureCircleActiveSlideInitialized(ensureSeq, 3);
      };
      window.setTimeout(finalizeAfterRelease, speed + 50);
    });

    // 遷移開始時にも適用（スライド中に前後が見えるように）
    swiper.on('slideChangeTransitionStart', () => {
      beginCircleSwiping();
      // 実際の移動方向を捕捉する（ループの最後↔先頭を index で判別できない問題を回避）。
      // activeIndex/previousIndex はスワイプ・タップ（slideNext/slidePrev）両方で更新され、
      // loopFix 前のこの時点では移動方向どおりの大小関係になる。
      {
        const dirSwiper = getSwiperInstance();
        if (dirSwiper
          && Number.isInteger(dirSwiper.activeIndex)
          && Number.isInteger(dirSwiper.previousIndex)
          && dirSwiper.activeIndex !== dirSwiper.previousIndex) {
          lastGroupSwipeIsForward = dirSwiper.activeIndex > dirSwiper.previousIndex;
        } else if (dirSwiper?.swipeDirection === 'next') {
          lastGroupSwipeIsForward = true;
        } else if (dirSwiper?.swipeDirection === 'prev') {
          lastGroupSwipeIsForward = false;
        }
      }
      circleEnsureSeq += 1;
      const ensureSeq = circleEnsureSeq;
      clearImageTimer();
      hideNonActiveSlideMedia();
      updateActiveHeaderVisibility({ showNeighbors: true });
      ensureActiveMediaHandlers();
      if (circleTransitionGuardTimerId != null) {
        clearTimeout(circleTransitionGuardTimerId);
        circleTransitionGuardTimerId = null;
      }
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      circleTransitionGuardTimerId = window.setTimeout(() => {
        circleTransitionGuardTimerId = null;
        ensureCircleActiveSlideInitialized(ensureSeq, 3);
      }, speed + 300);

      // アニメーション開始前に次グループへ freq 注入し、スワイプ中に c1 が一瞬見えるフラッシュを防ぐ。
      // slideChangeTransitionEnd より前に実行することで、アニメーション中のスライドDOMを freq で上書きできる。
      {
        const swiperInst = getSwiperInstance();
        const nextGroupIdx = Number.isFinite(swiperInst?.realIndex) ? swiperInst.realIndex : -1;
        const groups = window.storeeelItems?.contentGroups || [];
        const nextGroupId = groups[nextGroupIdx]?.id != null ? String(groups[nextGroupIdx].id) : null;
        runFreqGroupTransition(lastActiveGroupId, nextGroupId);
      }
    });

    swiper.on('slideChangeTransitionEnd', () => {
      const prevGroupId = lastActiveGroupId;
      // 遷移後はスワイプ状態を解除して、アクティブだけ表示に戻す
      endCircleSwiping();
      const ensureSeq = circleEnsureSeq;
      if (circleTransitionGuardTimerId != null) {
        clearTimeout(circleTransitionGuardTimerId);
        circleTransitionGuardTimerId = null;
      }

      // グループが変わった遷移なら、必ず先頭コンテンツから再生するよう強制フラグを立てる
      // （ensure の再適用で書き換わった位置を維持してしまい「途中から再生」になるのを防ぐ）。
      // 頻度挿入状態の更新・注入も applyCircleActiveSlideState より先に行う。
      const landedGroupId = getGroupIdFromSlide(
        document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active')
      );
      // 頻度挿入の注入（slideChangeTransitionStart 等で注入済みなら冪等にスキップされる）
      runFreqGroupTransition(prevGroupId, landedGroupId);

      applyCircleActiveSlideState();

      const currentSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!currentSlide) return;
      const currentGroupId = getGroupIdFromSlide(currentSlide);
      if (prevGroupId && currentGroupId && prevGroupId !== currentGroupId) {
        // グループを離れたら裏側で先頭コンテンツに戻す
        resetInactiveGroupToFirstContent(prevGroupId);
      }
      lastActiveGroupId = currentGroupId;
      const currentVideo = currentSlide.querySelector('video.storeeel-movie');

      // 遷移完了時にも再適用（ちらつき防止）
      hideNonActiveSlideMedia();
      updateActiveHeaderVisibility({ showNeighbors: false });

      document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide').forEach(() => {
        // 音量ボタンの状態を確認
        if (currentVideo) {
          checkVolumeButtonState();
        }
      });

      // 自動再生のためのイベントリスナーの再定義
      mobileAutoPlayNextAction();

      // 再生ボタンの状態を必ず停止ボタンにする
      const playButton = document.querySelector('.storeeel-menu-area-icons #start-desktop');
      if (playButton) {
        playButton.src = pauseImg;
        playButton.alt = 'pause';
        playButton.id = 'pause-desktop';
      }

      // loopFix 等で active が差し替わった場合に備えて、次tick以降も状態を監視して復旧する
      ensureCircleActiveSlideInitialized(ensureSeq, 3);
      window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, 3), 0);
      window.requestAnimationFrame(() => ensureCircleActiveSlideInitialized(ensureSeq, 3));
      window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, 3), 100);
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      window.setTimeout(() => ensureCircleActiveSlideInitialized(ensureSeq, 3), speed + 50);
      maybeShowTargetingQuestionnaireForCurrentContent();
    });
  } else {
    // スライド型の対応(うまく動かなかったため、グループ型と切り分けています。)
    const updateActiveHeaderVisibility = () => {
      if (!window.storeeelItems?.isMobile) return;
      const allWrappers = document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide .storeeel-header-wrapper');
      if (isReelLike(window.storeeelItems?.frame)) {
        // リール型・タイル型は各スライドにヘッダーを載せ、スワイプ時にコンテンツと一緒に動かす
        allWrappers.forEach((w) => { w.style.display = ''; });
        return;
      }
      allWrappers.forEach((w) => { w.style.display = 'none'; });
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!activeSlide) return;
      activeSlide.querySelectorAll('.storeeel-header-wrapper').forEach((w) => { w.style.display = ''; });
    };

    const applySquareActiveSlideState = () => {
      const s = getSwiperInstance();
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (!activeSlide) return;

      // 離脱したスライドの進捗バーを止める（裏で進み続けたバーが戻った時に一瞬見えるのを防ぐ）
      resetInactiveSlidesProgressBars(activeSlide);

      updateActiveHeaderVisibility();

      const groups = window.storeeelItems?.contentGroups || [];
      const pendingUpdate = window._storeeelSquarePendingPositionUpdate;
      const targetRealIndex = Number.isFinite(window._storeeelSquareTargetRealIndex)
        ? window._storeeelSquareTargetRealIndex
        : null;
      const realIndex = Number.isFinite(s?.realIndex) ? s.realIndex : (s?.activeIndex ?? 0);
      if (Number.isFinite(targetRealIndex) && targetRealIndex !== realIndex) {
        // 想定ターゲットと現在位置がずれている場合は、まずターゲットへ再遷移して位置を揃える
        if (typeof s?.slideToLoop === 'function') {
          s.slideToLoop(targetRealIndex, 0);
        } else if (typeof s?.slideTo === 'function') {
          s.slideTo(targetRealIndex, 0);
        }
        return;
      }
      const chosenIndex = Number.isFinite(targetRealIndex) ? targetRealIndex : realIndex;
      const safeIndex = Math.max(0, Math.min(chosenIndex, Math.max(0, groups.length - 1)));
      const gid = pendingUpdate?.iconId ?? groups?.[safeIndex]?.id;
      if (gid != null) window.iconId = String(gid);
      window.moviePosition = pendingUpdate?.moviePosition ?? 0;
      window._storeeelSquarePendingPositionUpdate = null;
      window._storeeelSquareTargetRealIndex = null;

      // 表示中のスライドを最優先に読み込み、落ち着いたら前後のスライドだけを先読みする
      loadReelMediaWindow(activeSlide);

      const activeVideo = activeSlide.querySelector('video.storeeel-movie');
      const activeImage = activeSlide.querySelector('img.storeeel-media-image');

      // 非アクティブ動画は確実に停止（高速スワイプ時の遅延ended対策）
      document.querySelectorAll('.storeeel-swiper-group-wrapper .swiper-slide').forEach((slide) => {
        if (slide === activeSlide) return;
        slide.classList.remove('active');
        slide.querySelectorAll('video').forEach((v) => {
          // ended リスナーが残っていると遅延発火で意図せず自動送りが走るため、非アクティブは必ず解除する
          if (v._endedListener) {
            try { v.removeEventListener('ended', v._endedListener); } catch { void 0; }
            delete v._endedListener;
          }
          try { v.pause(); } catch { void 0; }
          try { v.currentTime = 0; } catch { void 0; }
        });
      });
      activeSlide.classList.add('active');
      const activeWrapper = activeSlide.querySelector('.slide-media-wrapper');
      showMediaLoadingShimmer(activeWrapper);
      // 初期表示・スワイプ遷移ともに、タップ遷移時と同じCTA更新経路を通して高さを一致させる
      updateCtaButtonAction(activeSlide);

      if (activeVideo) {
        // iPhone は黒画面防止のため前後スライドの動画を「先頭から」プレビュー再生している。
        // ここで 0 へ巻き戻すと「少し進んだ状態→最初に戻る」というジャンプに見えるため、
        // プレビューで進んだ動画（currentTime>0）はその位置からそのまま連続再生させる。
        // ※ 着地直後は Swiper の active 切替と stopPreviewVideos のタイミングで一瞬 pause されることがあるため、
        //   pause 状態は条件にしない（pause されていても play() で同じ位置から再開する）。
        const keepPreviewPosition = window.isIphone
          && Number.isFinite(activeVideo.currentTime)
          && activeVideo.currentTime > 0
          && (!Number.isFinite(activeVideo.duration) || activeVideo.currentTime < activeVideo.duration);
        if (!keepPreviewPosition) {
          try { activeVideo.currentTime = 0; } catch { void 0; }
        }
        if (activeVideo._onLoadedMetadataForProgress) {
          activeVideo.removeEventListener('loadedmetadata', activeVideo._onLoadedMetadataForProgress);
          activeVideo._onLoadedMetadataForProgress = null;
        }
        if (activeVideo._onCanPlayForProgress) {
          activeVideo.removeEventListener('canplay', activeVideo._onCanPlayForProgress);
          activeVideo._onCanPlayForProgress = null;
        }
        if (activeVideo._onPlayingForProgress) {
          activeVideo.removeEventListener('playing', activeVideo._onPlayingForProgress);
          activeVideo._onPlayingForProgress = null;
        }

        const isStillActive = () => {
          const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          return !!slide && slide === activeSlide && slide.contains(activeVideo);
        };

        const cleanup = () => {
          if (activeVideo._onLoadedMetadataForProgress) {
            activeVideo.removeEventListener('loadedmetadata', activeVideo._onLoadedMetadataForProgress);
            activeVideo._onLoadedMetadataForProgress = null;
          }
          if (activeVideo._onCanPlayForProgress) {
            activeVideo.removeEventListener('canplay', activeVideo._onCanPlayForProgress);
            activeVideo._onCanPlayForProgress = null;
          }
          if (activeVideo._onPlayingForProgress) {
            activeVideo.removeEventListener('playing', activeVideo._onPlayingForProgress);
            activeVideo._onPlayingForProgress = null;
          }
        };

        const startProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return;
          }
          const duration = Number(activeVideo.duration);
          if (!Number.isFinite(duration) || duration <= 0) return;
          // プレビュー再生で動画が進んだ位置から再生する場合、進捗バーも同じだけ進めた状態から始める
          // （0 から始めると動画とバーがズレるため）。
          const startOffset = (activeVideo.currentTime > 0 && activeVideo.currentTime < duration)
            ? activeVideo.currentTime
            : 0;
          animateCurrentProgressBarInElement(duration, 0, activeSlide, startOffset);
          hideMediaLoadingShimmer(activeWrapper);
          cleanup();
        };

        const tryStartProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return false;
          }
          if (activeVideo.paused || activeVideo.ended) return false;
          if (activeVideo.readyState < 2) return false;
          startProgress();
          return true;
        };

        // 再生開始まで進捗を待機
        prepareCurrentProgressBarInElement(0, activeSlide);
        activeVideo._onLoadedMetadataForProgress = () => { if (tryStartProgress()) cleanup(); };
        activeVideo._onPlayingForProgress = () => { if (tryStartProgress()) cleanup(); };
        activeVideo.addEventListener('loadedmetadata', activeVideo._onLoadedMetadataForProgress);
        activeVideo.addEventListener('playing', activeVideo._onPlayingForProgress);
        tryStartProgress();

        // 再生自体は失敗しても致命ではない（進捗バー/自動送りは別途動作）
        try {
          const playPromise = activeVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => { void 0; });
          }
        } catch { void 0; }
        checkVolumeButtonState();
        // active video が確定したタイミングでも ended リスナーを再付与して取りこぼしを防ぐ
        mobileAutoPlayNextAction();
      } else if (activeImage) {
        if (activeImage._onLoadForProgress) {
          activeImage.removeEventListener('load', activeImage._onLoadForProgress);
          activeImage._onLoadForProgress = null;
        }
        if (activeImage._onErrorForProgress) {
          activeImage.removeEventListener('error', activeImage._onErrorForProgress);
          activeImage._onErrorForProgress = null;
        }

        const isStillActive = () => {
          const slide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
          return !!slide && slide === activeSlide && slide.contains(activeImage);
        };

        const cleanup = () => {
          if (activeImage._onLoadForProgress) {
            activeImage.removeEventListener('load', activeImage._onLoadForProgress);
            activeImage._onLoadForProgress = null;
          }
          if (activeImage._onErrorForProgress) {
            activeImage.removeEventListener('error', activeImage._onErrorForProgress);
            activeImage._onErrorForProgress = null;
          }
        };

        const startProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return;
          }
          animateCurrentProgressBarInElement(5, 0, activeSlide);
          hideMediaLoadingShimmer(activeWrapper);
          cleanup();
        };

        const tryStartProgress = () => {
          if (!isStillActive()) {
            cleanup();
            hideMediaLoadingShimmer(activeWrapper);
            return false;
          }
          if (!isMediaImageReady(activeImage)) return false;
          startProgress();
          return true;
        };

        // 読み込み完了後に進捗を開始する
        prepareCurrentProgressBarInElement(0, activeSlide);
        activeImage._onLoadForProgress = () => { tryStartProgress(); };
        activeImage._onErrorForProgress = () => { cleanup(); };
        activeImage.addEventListener('load', activeImage._onLoadForProgress);
        activeImage.addEventListener('error', activeImage._onErrorForProgress);
        tryStartProgress();
      }

      // 計測とローカルストレージ保存（squareはgroup_id=nullの既存仕様）
      const mediaEl = activeVideo || activeImage;
      const mediaName = mediaEl?.getAttribute('media-name');
      const mediaId = mediaEl?.getAttribute('media-id');
      const mediaAssetType = activeWrapper?.dataset?.assetType;
      const isFreqInsert = activeWrapper?.dataset?.isFrequencyInsertContent === 'true';
      // media_id が空のイベントは集計に乗らないため送らない（isTrackableMediaId のコメント参照）
      if (!isTrackableMediaId(mediaId)) return;

      if (isFreqInsert) {
        const freqContentId = activeWrapper?.dataset?.frequencyInsertContentId || mediaId;
        trackStoreeelEvent('storeeel_frequency_insert_media_view', {
          storeeel_name: mediaName,
          ...buildMediaAssetIdParam(freqContentId, mediaAssetType),
          campaign_id: window.storeeelItems.id,
        });
      } else {
        trackStoreeelEvent('storeeel_media_asset_view', buildMediaAssetViewParams({
          storeeelName: mediaName,
          groupId: null,
          mediaId,
          campaignId: window.storeeelItems.id,
          mediaAssetType,
        }));
      }
      localStorageRepositories.get("mediaView").set(
        mediaId,
        window.storeeelItems.id,
        null,
        mediaAssetType
      );
    };

    const isPendingSquareQuestionnairePositionMatched = () => {
      // pending / DOM の group-id はどちらも contentGroup.id そのもの。フィードフォルダの仮想グループIDは
      // `feed-folder-group-{contentId}-{feedProductId}-{index}` とハイフンを含むため分割してはいけない
      const pendingGroupId = String(window._storeeelSquarePendingQuestionnaireGroupId || '');
      const pendingIndex = Number.isFinite(window._storeeelSquarePendingQuestionnaireIndex)
        ? window._storeeelSquarePendingQuestionnaireIndex
        : null;
      if (!pendingGroupId && pendingIndex == null) return true;

      const s = getSwiperInstance();
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      const activeGroupId = String(
        activeSlide?.querySelector('.slide-media-wrapper')?.getAttribute('group-id')
        || ''
      );
      const realIndex = Number.isFinite(s?.realIndex) ? s.realIndex : (s?.activeIndex ?? 0);
      const groups = window.storeeelItems?.contentGroups || [];
      const targetIndex = pendingIndex ?? groups.findIndex((group) => String(group?.id) === pendingGroupId);

      return (!pendingGroupId || activeGroupId === pendingGroupId)
        && (!Number.isFinite(targetIndex) || realIndex === targetIndex);
    };

    const moveToPendingSquareQuestionnairePosition = () => {
      const pendingIndex = Number.isFinite(window._storeeelSquarePendingQuestionnaireIndex)
        ? window._storeeelSquarePendingQuestionnaireIndex
        : null;
      if (!Number.isFinite(pendingIndex)) return;

      const s = getSwiperInstance();
      if (s?.animating) return;
      if (typeof s?.slideToLoop === 'function') {
        s.slideToLoop(pendingIndex, 0);
      } else if (typeof s?.slideTo === 'function') {
        s.slideTo(pendingIndex, 0);
      }
    };

    const clearPendingSquareQuestionnairePosition = () => {
      window._storeeelSquarePendingQuestionnaireGroupId = null;
      window._storeeelSquarePendingQuestionnaireIndex = null;
    };

    const ensureActiveVideoEndedListener = () => {
      const activeVideo = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active video.storeeel-movie');
      if (!activeVideo) return false;
      if (typeof activeVideo._endedListener === 'function') return true;
      mobileAutoPlayNextAction();
      return typeof activeVideo._endedListener === 'function';
    };

    const scheduleEnsureActiveVideoEndedListener = (remainingRetries = 12, retryDelay = 80) => {
      // loopFix 等で active 差し替えが遅れた場合でも ended リスナーを取りこぼさないように再試行する
      if (ensureActiveVideoEndedListener()) {
        return;
      }
      if (remainingRetries <= 0) {
        return;
      }
      window.setTimeout(() => {
        scheduleEnsureActiveVideoEndedListener(remainingRetries - 1, retryDelay);
      }, retryDelay);
    };

    // iOS Safari では、Swiper の transform 中に「前後スライドの video が真っ黒」になることがあるため、
    // スワイプ中だけ前後スライドの video を再生して描画を維持する（終了時に停止）。
    const SQUARE_SWIPING_CLASS = 'storeeel--swiping';
    const getSquareRoot = () => document.querySelector('.storeeel-modal > .storeeel-swiper-group-wrapper');

    let squareIsSwiping = false;
    let squareSwipingReleaseTimerId = null;
    const squarePreviewVideos = new Set();

    const playPreviewVideo = (videoEl) => {
      if (!window.isIphone) return;
      if (!(videoEl instanceof HTMLVideoElement)) return;
      if (squarePreviewVideos.has(videoEl)) return;
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      if (activeSlide && activeSlide.contains(videoEl)) return;

      // 非アクティブは常にミュート（音声競合・意図しない再生の防止）
      try { videoEl.muted = true; } catch { void 0; }
      try { videoEl.setAttribute('muted', ''); } catch { void 0; }
      try { videoEl.setAttribute('playsinline', ''); } catch { void 0; }
      try { videoEl.setAttribute('webkit-playsinline', ''); } catch { void 0; }
      try { videoEl.preload = 'auto'; } catch { void 0; }
      // 先読みの順番待ちで src が保留されていても、覗いている隣のスライドは今すぐ読み込む
      ensureReelSlideMediaLoaded(videoEl.closest('.swiper-slide'));

      // プレビューは必ず先頭から再生する。こうしておけば「currentTime>0＝プレビューで進んだ分」と判定でき、
      // 着地時にその位置から連続再生しても動画の先頭から途切れず再生されているように見える。
      try { videoEl.currentTime = 0; } catch { void 0; }

      try {
        const p = videoEl.play?.();
        if (p?.catch) p.catch(() => { void 0; });
      } catch { void 0; }

      squarePreviewVideos.add(videoEl);
    };

    const stopPreviewVideos = () => {
      const activeSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide-active');
      squarePreviewVideos.forEach((videoEl) => {
        if (activeSlide && activeSlide.contains(videoEl)) return;
        try { videoEl.pause?.(); } catch { void 0; }
      });
      squarePreviewVideos.clear();
    };

    const updateSquarePreviewVideos = () => {
      if (!window.isIphone) return;
      const nextSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-next');
      const prevSlide = document.querySelector('.storeeel-swiper-group-wrapper .swiper-slide.swiper-slide-prev');
      const targetVideos = new Set([
        ...Array.from(nextSlide?.querySelectorAll?.('video.storeeel-movie') || []),
        ...Array.from(prevSlide?.querySelectorAll?.('video.storeeel-movie') || []),
      ].filter((v) => v instanceof HTMLVideoElement));

      // 対象スライドの動画だけ再生し、不要になったプレビュー再生は停止する
      squarePreviewVideos.forEach((videoEl) => {
        if (targetVideos.has(videoEl)) return;
        try { videoEl.pause?.(); } catch { void 0; }
        squarePreviewVideos.delete(videoEl);
      });
      targetVideos.forEach((videoEl) => playPreviewVideo(videoEl));
    };

    const beginSquareSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      if (squareSwipingReleaseTimerId != null) {
        clearTimeout(squareSwipingReleaseTimerId);
        squareSwipingReleaseTimerId = null;
      }
      squareIsSwiping = true;
      getSquareRoot()?.classList?.add?.(SQUARE_SWIPING_CLASS);
    };

    const endSquareSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      const swiperInstance = getSwiperInstance();
      if (swiperInstance?.touchEventsData?.isTouched) return;
      if (squareSwipingReleaseTimerId != null) {
        clearTimeout(squareSwipingReleaseTimerId);
        squareSwipingReleaseTimerId = null;
      }
      squareIsSwiping = false;
      getSquareRoot()?.classList?.remove?.(SQUARE_SWIPING_CLASS);
      stopPreviewVideos();
    };

    const scheduleEndSquareSwiping = () => {
      if (!window.storeeelItems?.isMobile) return;
      if (squareSwipingReleaseTimerId != null) clearTimeout(squareSwipingReleaseTimerId);
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      squareSwipingReleaseTimerId = window.setTimeout(() => {
        squareSwipingReleaseTimerId = null;
        endSquareSwiping();
      }, speed + 50);
    };

    swiper.on('touchStart', () => {
      beginSquareSwiping();
    });
    swiper.on('sliderMove', () => {
      if (!squareIsSwiping) beginSquareSwiping();
      updateSquarePreviewVideos();
    });
    swiper.on('touchEnd', () => {
      scheduleEndSquareSwiping();
    });

    let squareAutoAdvanceFallbackTimerId = null;
    const scheduleSquareAutoAdvanceFallback = () => {
      if (!window._storeeelSquareAutoAdvanceInProgress) return;
      if (squareAutoAdvanceFallbackTimerId != null) {
        clearTimeout(squareAutoAdvanceFallbackTimerId);
      }
      const speed = typeof swiper?.params?.speed === 'number' ? swiper.params.speed : 500;
      squareAutoAdvanceFallbackTimerId = window.setTimeout(() => {
        squareAutoAdvanceFallbackTimerId = null;
        if (!window._storeeelSquareAutoAdvanceInProgress) return;
        const s = getSwiperInstance();
        const targetRealIndex = Number.isFinite(window._storeeelSquareTargetRealIndex)
          ? window._storeeelSquareTargetRealIndex
          : null;
        const currentRealIndex = Number.isFinite(s?.realIndex) ? s.realIndex : (s?.activeIndex ?? null);
        if (targetRealIndex == null || currentRealIndex !== targetRealIndex) return;
        if (s?.animating) return;
        applySquareActiveSlideState();
        // transitionEnd が来ない場合でも状態を確定させる
        window._storeeelSquareAutoAdvanceInProgress = false;
        if (window._storeeelSquareAutoAdvanceGuardTimer) {
          clearTimeout(window._storeeelSquareAutoAdvanceGuardTimer);
          window._storeeelSquareAutoAdvanceGuardTimer = null;
        }
        if (isReelLike(window.storeeelItems?.frame) && window._storeeelSquarePendingAutoPlay) {
          window._storeeelSquarePendingAutoPlay = false;
          mobileAutoPlayNextAction();
        }
        mobileAutoPlayNextAction();
        scheduleEnsureActiveVideoEndedListener();
      }, speed + 200);
    };

    swiper.on('slideChange', () => {
      clearImageTimer(); // 画像タイマーをクリア
      updateActiveHeaderVisibility();
      // 手動スワイプ時は自動送り用ターゲットをクリア（ユーザー操作を優先）
      if (isReelLike(window.storeeelItems?.frame) && !window._storeeelSquareAutoAdvanceInProgress) {
        window._storeeelSquareTargetRealIndex = null;
      }
      if (isReelLike(window.storeeelItems?.frame)) {
        scheduleSquareAutoAdvanceFallback();
      }
    });
    swiper.on('slideChangeTransitionEnd', () => {
      endSquareSwiping();
      updateActiveHeaderVisibility();
      if (!isPendingSquareQuestionnairePositionMatched()) {
        moveToPendingSquareQuestionnairePosition();
        return;
      }
      applySquareActiveSlideState();
      clearPendingSquareQuestionnairePosition();

      // 自動送りでの多重遷移ガード解除
      if (squareAutoAdvanceFallbackTimerId != null) {
        clearTimeout(squareAutoAdvanceFallbackTimerId);
        squareAutoAdvanceFallbackTimerId = null;
      }
      window._storeeelSquareAutoAdvanceInProgress = false;
      if (window._storeeelSquareAutoAdvanceGuardTimer) {
        clearTimeout(window._storeeelSquareAutoAdvanceGuardTimer);
        window._storeeelSquareAutoAdvanceGuardTimer = null;
      }
      if (isReelLike(window.storeeelItems?.frame) && window._storeeelSquarePendingAutoPlay) {
        window._storeeelSquarePendingAutoPlay = false;
        mobileAutoPlayNextAction();
      }
      mobileAutoPlayNextAction();
      maybeShowTargetingQuestionnaireForCurrentContent();
      // loop（複製スライド）環境では、transitionEnd 直後に loopFix 等で active が差し替わり、
      // ended リスナーが「入れ替え前のDOM」に付いたままになることがある。
      // 次の macrotask でアクティブ動画のリスナー欠落だけを補完し、二重付与/タイミング競合を避ける。
      scheduleEnsureActiveVideoEndedListener();

      // 再生ボタンの状態を必ず停止ボタンにする
      const playButton = document.querySelector('.storeeel-menu-area-icons #start-desktop');
      if (playButton) {
        playButton.src = pauseImg;
        playButton.alt = 'pause';
        playButton.id = 'pause-desktop';
      }
    });

    // 初期表示時にもアクティブスライド状態を確定しておく
    const ensureInitialSquarePositionAndQuestionnaire = (remainingAttempts = 12) => {
      updateActiveHeaderVisibility();
      if (!isPendingSquareQuestionnairePositionMatched()) {
        moveToPendingSquareQuestionnairePosition();
        if (remainingAttempts <= 0) return;
        window.setTimeout(() => {
          ensureInitialSquarePositionAndQuestionnaire(remainingAttempts - 1);
        }, 50);
        return;
      }
      applySquareActiveSlideState();
      clearPendingSquareQuestionnairePosition();
      // 初期化直後は Swiper の active 確定が遅れることがあるため、リスナー欠落時のみ補完する
      scheduleEnsureActiveVideoEndedListener();
      maybeShowTargetingQuestionnaireForCurrentContent();
    };

    window.requestAnimationFrame(() => {
      ensureInitialSquarePositionAndQuestionnaire();
    });
  }
}
